import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../services/query/queryKeys';
import { appwriteQueryFns } from '../services/query/appwriteQueryFns';
import { backendProvider } from '../services/backend';
import { teamInviteService } from '../services/backend/appwrite/teamInviteService';
import { geoService } from '../services/geo.service';
import { useAuth } from '../contexts/AuthContext';

/**
 * Eagerly prefetches user preferences, team preferences, team ID, geo data,
 * branding permissions, and team roster as soon as the user identity is known.
 * Everything Settings needs is in cache before the user clicks their avatar.
 */
export function usePrefetchPreferences() {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid) return;

    // Fire all prefetches in parallel — cache fills before the user needs it.
    // staleTime 5 min: this effect re-runs on every mount of InnerApp (route
    // changes to the layouts that mount it), so short windows here previously
    // re-fired /account/prefs + /teams every 30s. The auth-service caches
    // (AppwriteAuthService) absorb the fan-out; these windows just avoid
    // re-firing the prefetch itself.
    void Promise.allSettled([
      // User + team preferences
      queryClient.prefetchQuery({
        queryKey: queryKeys.preferences.user(uid),
        queryFn: () => appwriteQueryFns.preferences.user(uid),
        staleTime: 5 * 60 * 1000,
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.preferences.team(uid),
        queryFn: () => appwriteQueryFns.preferences.team(),
        staleTime: 5 * 60 * 1000,
      }),
      // Team ID (used in user menu)
      queryClient.prefetchQuery({
        queryKey: ['team', 'id', uid],
        queryFn: () => backendProvider.auth.getTeamId(),
        staleTime: 5 * 60 * 1000,
      }),
      // Team roster (used in Settings → Team tab)
      queryClient.prefetchQuery({
        queryKey: ['team', 'roster', uid],
        queryFn: async () => {
          const response = await teamInviteService.getTeamRoster({
            admin_user_id: uid,
            admin_email: currentUser?.email || undefined,
          });
          return {
            teamMembers: (response.data?.team_members || []) as any[],
            teamInvitations: (response.data?.team_invitations || []) as any[],
          };
        },
        staleTime: 5 * 60 * 1000,
      }),
      // Branding access + partner flag (Settings → Branding tab)
      queryClient.prefetchQuery({
        queryKey: ['branding', 'access', uid],
        queryFn: () => backendProvider.auth.getTeamMembershipRoles(),
        staleTime: 5 * 60 * 1000,
      }),
      queryClient.prefetchQuery({
        queryKey: ['branding', 'partner-flag', uid],
        queryFn: () => backendProvider.auth.getTeamPreferences(),
        staleTime: 5 * 60 * 1000,
      }),
      // Geo data — used by Account settings (country/state selects)
      queryClient.prefetchQuery({
        queryKey: ['geo', 'countries'],
        queryFn: () => geoService.listCountries(),
        staleTime: Infinity,
      }),
    ]);
  }, [currentUser?.uid, queryClient]);
}